import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from '@tanstack/react-query';

import { APIKey } from 'constants/apiKeys';
import { useShiftNoteCapture } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import useKeyboardInset from 'shiftNotes/hooks/useKeyboardInset';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';

import Overlay from 'shiftNotes/styles/NoteCaptureSection.style';

import NoteCaptureReady from './NoteCaptureReady';
import NoteCaptureRecording from './NoteCaptureRecording';
import NoteCaptureProcessing from './NoteCaptureProcessing';
import NoteCaptureWriting from './NoteCaptureWriting';
import NoteCaptureResponse from './NoteCaptureResponse';
import NoteCaptureErrorCard from './NoteCaptureErrorCard';

export type NoteCaptureState =
  | 'ready'
  | 'recording'
  | 'writing'
  | 'error'
  | 'response';

export interface NoteCaptureProps {
  mobileView?: boolean;
  onClose?: () => void;
  suppressReady?: boolean;
}

const CREATED_CARD_CLOSE_DELAY_MS = 200;
const CREATED_CARD_MAX_WAIT_MS = 2_000;

type SubmissionCloseState = {
  waitingForInitialRefetch: boolean;
  initialRefetchSeen: boolean;
  closeScheduled: boolean;
};

export default function NoteCaptureSection({
  mobileView,
  onClose,
  suppressReady = false,
}: NoteCaptureProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const shiftNotesCapture = usePosthogCapture();
  const {
    isRecording,
    isRecordingPaused,
    onRecordingToggle,
    onCancelRecording,
    pauseRecording,
    resumeRecording,
    submitRecording,
    mediaError,
    showCreatedCard,
    createdNotes,
    closeCreatedCard,
    audioLevel,
    submitTextNote,
    isWritingMode,
    writingModeInitialText,
    openWritingMode,
    closeWritingMode,
    exitResolveMode,
    clearAttachments,
    pendingUploads,
    markPendingSilent,
  } = useShiftNoteCapture();

  const [isAudioCapture, setIsAudioCapture] = useState(false);
  const [isSwitchingToVoice, setIsSwitchingToVoice] = useState(false);
  const [isAwaitingSubmission, setIsAwaitingSubmission] = useState(false);
  const keyboardInset = useKeyboardInset();
  const [dismissedMediaErrorKey, setDismissedMediaErrorKey] = useState<
    string | null
  >(null);

  const errorUploadCount = pendingUploads.filter(
    (u) => u.status === 'error'
  ).length;
  const prevErrorUploadCountRef = useRef(errorUploadCount);

  useEffect(() => {
    if (!isAwaitingSubmission) {
      prevErrorUploadCountRef.current = errorUploadCount;
      return;
    }
    if (showCreatedCard) {
      setIsAwaitingSubmission(false);
      prevErrorUploadCountRef.current = errorUploadCount;
      return;
    }
    if (errorUploadCount > prevErrorUploadCountRef.current) {
      setIsAwaitingSubmission(false);
      prevErrorUploadCountRef.current = errorUploadCount;
    }
  }, [isAwaitingSubmission, showCreatedCard, errorUploadCount]);

  const mediaErrorKey = mediaError
    ? `media:${mediaError.message || 'unknown'}`
    : null;
  const isMediaErrorDismissed = dismissedMediaErrorKey === mediaErrorKey;

  useEffect(() => {
    if (isRecording) {
      setIsAudioCapture(true);
    }
  }, [isRecording]);

  useEffect(() => {
    if (!mediaError) {
      setDismissedMediaErrorKey(null);
    }
  }, [mediaError]);

  const fetchingEvents = useIsFetching({ queryKey: [APIKey.EVENT] });
  const fetchingInsights = useIsFetching({
    queryKey: [APIKey.INSIGHTS_EVENTS],
  });
  const isRefetchingNotes = fetchingEvents + fetchingInsights > 0;
  const submissionCloseRef = useRef<SubmissionCloseState | null>(null);

  useEffect(() => {
    if (mobileView || !showCreatedCard) return undefined;
    const timer = setTimeout(closeCreatedCard, CREATED_CARD_MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, [mobileView, showCreatedCard, closeCreatedCard]);

  useEffect(() => {
    if (mobileView || !showCreatedCard) {
      submissionCloseRef.current = null;
      return undefined;
    }

    if (!submissionCloseRef.current) {
      submissionCloseRef.current = {
        waitingForInitialRefetch: isRefetchingNotes,
        initialRefetchSeen: false,
        closeScheduled: false,
      };
    }

    const state = submissionCloseRef.current;
    if (state.closeScheduled) return undefined;

    if (state.waitingForInitialRefetch) {
      if (isRefetchingNotes) {
        state.initialRefetchSeen = true;
        return undefined;
      }
      state.waitingForInitialRefetch = false;
    }

    state.closeScheduled = true;
    const timer = setTimeout(closeCreatedCard, CREATED_CARD_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [mobileView, showCreatedCard, isRefetchingNotes, closeCreatedCard]);

  const showMediaError = Boolean(mediaError) && !isMediaErrorDismissed;

  const state: NoteCaptureState = useMemo(() => {
    if (isRecording) return 'recording';
    if (isWritingMode) return 'writing';
    if (showCreatedCard && createdNotes.length > 0) return 'response';
    if (showMediaError) return 'error';
    return 'ready';
  }, [
    showCreatedCard,
    createdNotes.length,
    isRecording,
    isWritingMode,
    showMediaError,
  ]);

  const mediaErrorCard = mediaError
    ? { type: 'media' as const, message: mediaError.message }
    : null;

  const showErrorCardInState =
    mediaErrorCard && (state === 'recording' || state === 'writing');

  const handleDismissMediaError = useCallback(() => {
    if (mediaErrorKey) {
      setDismissedMediaErrorKey(mediaErrorKey);
    }
    closeWritingMode();
  }, [mediaErrorKey, closeWritingMode]);

  const handleRetryMediaError = useCallback(() => {
    if (isAudioCapture) {
      handleDismissMediaError();
      onRecordingToggle();
      return;
    }
    if (mediaErrorKey) {
      setDismissedMediaErrorKey(mediaErrorKey);
    }
    openWritingMode();
  }, [
    isAudioCapture,
    handleDismissMediaError,
    onRecordingToggle,
    mediaErrorKey,
    openWritingMode,
  ]);

  const retryLabel = isAudioCapture
    ? t('noteCaptureCard.newRecording')
    : t('noteCaptureCard.tryAgain');

  const handleCancel = () => {
    if (state === 'recording') {
      onCancelRecording();
      clearAttachments();
      exitResolveMode();
    }
  };

  const handleConfirm = () => {
    if (state === 'recording') {
      setIsAudioCapture(true);
      if (mobileView) setIsAwaitingSubmission(true);
      submitRecording();
    }
  };

  const handleTextSubmit = (text: string) => {
    setIsAudioCapture(false);
    if (mobileView) setIsAwaitingSubmission(true);
    submitTextNote(text);
    closeWritingMode();
  };

  const handleTextCancel = () => {
    closeWritingMode();
    exitResolveMode();
  };

  const handleSwitchToWriting = useCallback(() => {
    shiftNotesCapture.captureModeToggled({ from: 'voice', to: 'text' });
    if (isRecording) {
      onCancelRecording();
    }
    setIsAudioCapture(false);
    openWritingMode();
  }, [isRecording, onCancelRecording, openWritingMode, shiftNotesCapture]);

  const handleSwitchToVoice = useCallback(() => {
    shiftNotesCapture.captureModeToggled({ from: 'text', to: 'voice' });
    setIsAudioCapture(true);
    setIsSwitchingToVoice(true);
    onRecordingToggle();
  }, [onRecordingToggle, shiftNotesCapture]);

  useEffect(() => {
    if (!isSwitchingToVoice) return;
    if (isRecording || mediaError) {
      closeWritingMode();
      setIsSwitchingToVoice(false);
    }
  }, [isSwitchingToVoice, isRecording, mediaError, closeWritingMode]);

  const handleClose = () => {
    if (state === 'response') {
      closeCreatedCard();
    }
    onClose?.();
  };

  const mobileBridgingSubmission =
    mobileView && isAwaitingSubmission && state === 'ready';

  if (state === 'ready' && !mobileBridgingSubmission) {
    if (suppressReady) return null;
    return <NoteCaptureReady mobileView={mobileView} />;
  }

  // Mobile-only overlay states — single persistent overlay swaps children
  if (mobileView) {
    const bridgeUpload = pendingUploads[pendingUploads.length - 1];
    let mobileContent: JSX.Element | null = null;
    if (showErrorCardInState && mediaErrorCard) {
      mobileContent = (
        <NoteCaptureErrorCard
          error={mediaErrorCard}
          isAudioCapture={isAudioCapture}
          onDismiss={handleDismissMediaError}
          onRetry={handleRetryMediaError}
          retryLabel={retryLabel}
        />
      );
    } else if (state === 'recording') {
      mobileContent = (
        <NoteCaptureRecording
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          onSwitchToWriting={handleSwitchToWriting}
          onPause={pauseRecording}
          onResume={resumeRecording}
          audioLevel={audioLevel}
          isAudioCapture={isAudioCapture}
          isPaused={isRecordingPaused}
        />
      );
    } else if (state === 'writing') {
      mobileContent = (
        <NoteCaptureWriting
          onCancel={handleTextCancel}
          onSubmit={handleTextSubmit}
          onSwitchToVoice={handleSwitchToVoice}
          initialValue={writingModeInitialText}
        />
      );
    } else if (state === 'response') {
      mobileContent = (
        <NoteCaptureResponse notes={createdNotes} onClose={handleClose} />
      );
    } else if (state === 'error' && mediaErrorCard) {
      mobileContent = (
        <NoteCaptureErrorCard
          error={mediaErrorCard}
          isAudioCapture={isAudioCapture}
          onDismiss={handleDismissMediaError}
          onRetry={handleRetryMediaError}
          retryLabel={retryLabel}
        />
      );
    } else if (mobileBridgingSubmission && bridgeUpload) {
      mobileContent = <NoteCaptureProcessing upload={bridgeUpload} />;
    }

    const handleOverlayClick = () => {
      if (state === 'response') {
        handleClose();
        return;
      }
      if (mobileBridgingSubmission) {
        setIsAwaitingSubmission(false);
        markPendingSilent();
      }
    };

    const overlayClickable = state === 'response' || mobileBridgingSubmission;

    return (
      <Overlay
        $keyboardInset={state === 'writing' ? keyboardInset : undefined}
        onClick={overlayClickable ? handleOverlayClick : undefined}
      >
        {mobileContent}
      </Overlay>
    );
  }

  if (showErrorCardInState && mediaErrorCard) {
    return (
      <NoteCaptureErrorCard
        error={mediaErrorCard}
        isAudioCapture={isAudioCapture}
        onDismiss={handleDismissMediaError}
        onRetry={handleRetryMediaError}
        retryLabel={retryLabel}
        inline
      />
    );
  }

  if (state === 'response') {
    if (suppressReady) return null;
    return <NoteCaptureReady mobileView={mobileView} />;
  }

  if (state === 'recording') {
    return (
      <NoteCaptureRecording
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onSwitchToWriting={handleSwitchToWriting}
        onPause={pauseRecording}
        onResume={resumeRecording}
        audioLevel={audioLevel}
        isAudioCapture={isAudioCapture}
        isPaused={isRecordingPaused}
        inline
      />
    );
  }

  if (state === 'error' && mediaErrorCard) {
    return (
      <NoteCaptureErrorCard
        error={mediaErrorCard}
        isAudioCapture={isAudioCapture}
        onDismiss={handleDismissMediaError}
        onRetry={handleRetryMediaError}
        retryLabel={retryLabel}
        inline
      />
    );
  }

  if (state === 'writing') {
    return (
      <NoteCaptureWriting
        onCancel={handleTextCancel}
        onSubmit={handleTextSubmit}
        onSwitchToVoice={handleSwitchToVoice}
        initialValue={writingModeInitialText}
        inline
      />
    );
  }

  if (suppressReady) return null;
  return <NoteCaptureReady mobileView={mobileView} />;
}
